import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebase-config';
import { Form, Button, Card, Alert, Row, Col, Spinner } from 'react-bootstrap';

function EditSystemPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: '',
    githubLink: '',
    siteLink: '',
    tags: '',
    rating: 5
  });
  const [imageFile, setImageFile] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch system data
  useEffect(() => {
    const fetchSystem = async () => {
      try {
        setLoading(true);
        const systemDoc = await getDoc(doc(db, 'systems', id));
        
        if (systemDoc.exists()) {
          const systemData = systemDoc.data();
          setFormData({
            title: systemData.title || '',
            description: systemData.description || '',
            author: systemData.author || '',
            githubLink: systemData.githubLink || '',
            siteLink: systemData.siteLink || '',
            tags: systemData.tags ? systemData.tags.join(', ') : '',
            rating: systemData.rating || 5
          });
          setCurrentImageUrl(systemData.imageUrl || '');
        } else {
          setError('System not found');
        }
      } catch (err) {
        console.error('Error fetching system:', err);
        setError('Failed to load system details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSystem();
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      setError('');
    }
  };

  const uploadImage = async (file) => {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
    const imageRef = ref(storage, `system_images/${fileName}`);
    
    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);
    return downloadURL;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Description is required');
      }

      let imageUrl = currentImageUrl;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      // Prepare tags array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Prepare system data for update
      const systemData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        author: formData.author.trim() || 'Anonymous',
        githubLink: formData.githubLink.trim(),
        siteLink: formData.siteLink.trim(),
        tags: tagsArray,
        rating: parseInt(formData.rating),
        imageUrl: imageUrl,
        updatedAt: serverTimestamp()
      };

      // Update in Firestore
      await updateDoc(doc(db, 'systems', id), systemData);
      
      setSuccess('System updated successfully!');
      
      // Optional: Redirect back to home after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);

    } catch (err) {
      console.error('Error updating system:', err);
      setError(err.message || 'Failed to update system. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <span className="ms-2">Loading system data...</span>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Edit System</h2>
        <Button variant="outline-secondary" onClick={() => navigate('/')}>
          Back to Dashboard
        </Button>
      </div>

      <Card className="add-system-card">
        <Card.Header>
          <h5 className="mb-0">Edit System: {formData.title}</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter system title"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Author</Form.Label>
                  <Form.Control
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Enter author name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter system description"
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GitHub Link</Form.Label>
                  <Form.Control
                    type="url"
                    name="githubLink"
                    value={formData.githubLink}
                    onChange={handleInputChange}
                    placeholder="https://github.com/..."
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Site Link</Form.Label>
                  <Form.Control
                    type="url"
                    name="siteLink"
                    value={formData.siteLink}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Tags</Form.Label>
                  <Form.Control
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="travel, culture, management (comma-separated)"
                  />
                  <Form.Text className="text-muted">
                    Separate multiple tags with commas
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Rating</Form.Label>
                  <Form.Control
                    as="select"
                    name="rating"
                    value={formData.rating}
                    onChange={handleInputChange}
                  >
                    <option value={1}>1 ⭐</option>
                    <option value={2}>2 ⭐⭐</option>
                    <option value={3}>3 ⭐⭐⭐</option>
                    <option value={4}>4 ⭐⭐⭐⭐</option>
                    <option value={5}>5 ⭐⭐⭐⭐⭐</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label>System Image</Form.Label>
              {currentImageUrl && (
                <div className="mb-3">
                  <img 
                    src={currentImageUrl} 
                    alt="Current system" 
                    style={{ maxHeight: "100px", display: "block", marginBottom: "10px" }} 
                    className="img-thumbnail"
                  />
                  <small className="text-muted">Current image</small>
                </div>
              )}
              <Form.Control
                type="file"
                id="imageFile"
                accept="image/*"
                onChange={handleImageChange}
              />
              <Form.Text className="text-muted">
                Upload a new image to replace the current one (max 5MB)
              </Form.Text>
            </Form.Group>

            <div className="d-grid">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={saving}
                size="lg"
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Updating System...
                  </>
                ) : (
                  'Update System'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default EditSystemPage;
